import React, { useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

const FieldOptionsTab = ({
  title,
  description,
  emptyMessage,
  fieldPlaceholder,
  optionPlaceholder,
  saveErrorMessage,
  activityPrefix,
  fields,
  onSave,
  logActivity,
}) => {
  const [fieldForm] = Form.useForm();
  const [optionForm] = Form.useForm();
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [optionModalOpen, setOptionModalOpen] = useState(false);
  const [optionFieldId, setOptionFieldId] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [saving, setSaving] = useState(false);

  const persist = async (updatedFields, activityType, activityDetail) => {
    setSaving(true);
    try {
      await onSave(updatedFields);
      if (activityType) {
        logActivity(activityType, activityDetail);
      }
    } catch {
      message.error(saveErrorMessage);
      throw new Error('save failed');
    } finally {
      setSaving(false);
    }
  };

  const nextFieldId = () => {
    if (!fields.length) return 1;
    return Math.max(...fields.map((f) => f.id)) + 1;
  };

  const nextOptionId = (field) => {
    const options = field.options || [];
    if (!options.length) return 1;
    return Math.max(...options.map((o) => o.id)) + 1;
  };

  const openAddField = () => {
    setEditingField(null);
    fieldForm.resetFields();
    setFieldModalOpen(true);
  };

  const openEditField = (field) => {
    setEditingField(field);
    fieldForm.setFieldsValue({
      label: field.label,
      status: field.status,
    });
    setFieldModalOpen(true);
  };

  const handleSaveField = () => {
    return fieldForm.validateFields().then(async (values) => {
      let updatedFields;

      if (editingField) {
        updatedFields = fields.map((f) =>
          f.id === editingField.id ? { ...f, ...values } : f
        );
        await persist(
          updatedFields,
          `update_${activityPrefix}_field`,
          `Updated ${activityPrefix} field: ${values.label}`
        );
        message.success('Field updated successfully');
      } else {
        updatedFields = [
          ...fields,
          {
            id: nextFieldId(),
            label: values.label,
            status: values.status,
            options: [],
          },
        ];
        await persist(
          updatedFields,
          `create_${activityPrefix}_field`,
          `Created ${activityPrefix} field: ${values.label}`
        );
        message.success('Field added successfully');
      }

      setFieldModalOpen(false);
    });
  };

  const handleDeleteField = async (field) => {
    const updatedFields = fields.filter((f) => f.id !== field.id);
    await persist(
      updatedFields,
      `delete_${activityPrefix}_field`,
      `Deleted ${activityPrefix} field: ${field.label}`
    );
    message.success(`Field "${field.label}" deleted`);
  };

  const openAddOption = (fieldId) => {
    setOptionFieldId(fieldId);
    setEditingOption(null);
    optionForm.resetFields();
    setOptionModalOpen(true);
  };

  const openEditOption = (fieldId, option) => {
    setOptionFieldId(fieldId);
    setEditingOption(option);
    optionForm.setFieldsValue({
      name: option.name,
      status: option.status,
    });
    setOptionModalOpen(true);
  };

  const handleSaveOption = () => {
    return optionForm.validateFields().then(async (values) => {
      const field = fields.find((f) => f.id === optionFieldId);
      if (!field) return Promise.reject();

      let updatedFields;

      if (editingOption) {
        updatedFields = fields.map((f) => {
          if (f.id !== optionFieldId) return f;
          return {
            ...f,
            options: (f.options || []).map((o) =>
              o.id === editingOption.id ? { ...o, ...values } : o
            ),
          };
        });
        await persist(
          updatedFields,
          `update_${activityPrefix}_option`,
          `Updated option "${values.name}" in ${field.label}`
        );
        message.success('Option updated successfully');
      } else {
        const newOption = {
          id: nextOptionId(field),
          ...values,
        };
        updatedFields = fields.map((f) => {
          if (f.id !== optionFieldId) return f;
          return {
            ...f,
            options: [...(f.options || []), newOption],
          };
        });
        await persist(
          updatedFields,
          `create_${activityPrefix}_option`,
          `Added option "${values.name}" to ${field.label}`
        );
        message.success('Option added successfully');
      }

      setOptionModalOpen(false);
    });
  };

  const handleDeleteOption = async (fieldId, option) => {
    const field = fields.find((f) => f.id === fieldId);
    const updatedFields = fields.map((f) => {
      if (f.id !== fieldId) return f;
      return {
        ...f,
        options: (f.options || []).filter((o) => o.id !== option.id),
      };
    });
    await persist(
      updatedFields,
      `delete_${activityPrefix}_option`,
      `Deleted option "${option.name}" from ${field?.label}`
    );
    message.success(`Option "${option.name}" deleted`);
  };

  const optionColumns = (fieldId) => [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Option',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <span className="font-medium">{name}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, option) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEditOption(fieldId, option)}
            className="text-blue-500"
          />
          <Popconfirm
            title="Delete this option?"
            onConfirm={() => handleDeleteOption(fieldId, option)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-gray-500 text-sm">{description}</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openAddField}
          className="bg-cyan-400 hover:bg-cyan-500"
          loading={saving}
        >
          Add Field
        </Button>
      </div>

      {fields.length === 0 ? (
        <Card className="text-center text-gray-500 py-8">{emptyMessage}</Card>
      ) : (
        fields.map((field) => (
          <Card
            key={field.id}
            className="shadow-sm"
            title={
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-base">{field.label}</span>
                  <Tag color={field.status === 'active' ? 'green' : 'red'}>
                    {field.status?.toUpperCase()}
                  </Tag>
                  <span className="text-gray-400 text-sm">
                    {(field.options || []).length} option(s)
                  </span>
                </div>
                <Space>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEditField(field)}
                  >
                    Edit Field
                  </Button>
                  <Popconfirm
                    title="Delete this field and all its options?"
                    onConfirm={() => handleDeleteField(field)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                      Delete Field
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            }
          >
            <div className="flex justify-end mb-3">
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openAddOption(field.id)}
              >
                Add Option
              </Button>
            </div>
            <Table
              columns={optionColumns(field.id)}
              dataSource={field.options || []}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: 'No options yet. Add one above.' }}
            />
          </Card>
        ))
      )}

      <Modal
        title={editingField ? 'Edit Field' : 'Add New Field'}
        open={fieldModalOpen}
        onCancel={() => setFieldModalOpen(false)}
        onOk={handleSaveField}
        okText={editingField ? 'Update' : 'Create'}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={fieldForm} layout="vertical" className="mt-4">
          <Form.Item
            label="Field Label"
            name="label"
            rules={[{ required: true, message: 'Field label is required' }]}
          >
            <Input placeholder={fieldPlaceholder} />
          </Form.Item>
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select status" allowClear>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingOption ? 'Edit Option' : 'Add New Option'}
        open={optionModalOpen}
        onCancel={() => setOptionModalOpen(false)}
        onOk={handleSaveOption}
        okText={editingOption ? 'Update' : 'Create'}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={optionForm} layout="vertical" className="mt-4">
          <Form.Item
            label="Option Name"
            name="name"
            rules={[{ required: true, message: 'Option name is required' }]}
          >
            <Input placeholder={optionPlaceholder} />
          </Form.Item>
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select status" allowClear>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FieldOptionsTab;
